import { SFX_CUES } from '../../data/sfx.js';
import { MusicDirector } from './MusicDirector.js';
import { MUSIC_THEMES, DEFAULT_MUSIC_THEME } from '../../data/musicThemes.js';

/**
 * Frequency multipliers the XP-mote tick walks through on consecutive
 * pickups — a major pentatonic run (1, 9/8, 5/4, 3/2, 5/3, 2) which has no
 * semitones in it, so any two steps landing close together still consonate.
 * That matters: motes arrive in unpredictable clumps, and a scale with a
 * tritone or a leading tone in it would sour every time two ticks collided.
 */
const MOTE_LADDER = [1, 1.125, 1.25, 1.5, 1.667, 2];

/**
 * Web Audio-based SFX/music playback, driven off game events rather than
 * call sites sprinkled through combat/spawning/progression. `bindRunEvents`
 * is called once per Game instance (each run gets a fresh EventBus) rather
 * than taking one in the constructor, since this system itself is a
 * singleton that outlives any single run (see the export at the bottom) —
 * it needs to keep working on the main menu, between runs, where there's
 * no run-scoped EventBus to listen to at all.
 *
 * The AudioContext is created lazily on the first sound, never at module
 * load: browsers block audio before a user gesture, and every call site
 * here only ever fires after a click/keypress, so this naturally satisfies
 * that without any special-casing.
 *
 * Music is delegated to MusicDirector (lookahead-scheduled, per-boss
 * themes, intensity layers — see musicThemes.js); this class stays the
 * single owner of the context, the mute/volume settings, and the SFX path.
 */
export class AudioSystem {
  constructor({ muted = false } = {}) {
    this.muted = muted;
    // Baseline mix levels the sliders scale from — kept well under 1 so a
    // slider at 100% doesn't clip/blow out the synthesized tones.
    this._sfxBase = 0.5;
    this._musicBase = 0.06;
    this.sfxVolume = 1;
    this.musicVolume = 1;
    this._ctx = null;
    this._music = null;
    this._sfxCompressor = null;
    this._lastSfxAt = {};
  }

  setMuted(muted) {
    this.muted = muted;
    if (muted) this.stopMusic();
  }

  setSfxVolume(v) {
    this.sfxVolume = Math.max(0, Math.min(1, v));
  }

  setMusicVolume(v) {
    this.musicVolume = Math.max(0, Math.min(1, v));
    this._music?.setVolume(this._musicBase * this.musicVolume);
  }

  _ensureContext() {
    if (!this._ctx) this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this._ctx.state === 'suspended') this._ctx.resume();
    return this._ctx;
  }

  // Shared SFX output bus: a soft-knee compressor in front of the
  // destination. A busy late-game fight stacks a dozen cues in a frame,
  // and hard-clipping the master is the ugliest possible failure mode —
  // this squashes peaks instead.
  _sfxBus(ctx) {
    if (!this._sfxCompressor) {
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -18;
      comp.knee.value = 12;
      comp.ratio.value = 5;
      comp.attack.value = 0.002;
      comp.release.value = 0.12;
      comp.connect(ctx.destination);
      this._sfxCompressor = comp;
    }
    return this._sfxCompressor;
  }

  /**
   * @param {string} id - key into SFX_CUES
   * @param {{pitchScale?: number}} [options] - multiplies the cue's frequency
   *   (and its sweep target) for this play only. Lets a caller walk a cue up a
   *   ladder — the XP-mote tick uses it so a stream of pickups arrives as a
   *   rising run rather than one flat repeated blip. Cue definitions stay
   *   single, static specs; the variation lives at the call site that knows
   *   why it's varying.
   */
  playSfx(id, options) {
    if (this.muted) return;
    const cue = SFX_CUES[id];
    if (!cue) return;
    // Per-cue rate limit (voice limiting): rapid-fire cues (weapon shots,
    // minion hits) retrigger far faster than the ear can separate —
    // dropping the extras keeps the mix clean and the node count bounded.
    if (cue.minInterval) {
      const now = performance.now();
      if (now - (this._lastSfxAt[id] ?? -Infinity) < cue.minInterval * 1000) return;
      this._lastSfxAt[id] = now;
    }
    const ctx = this._ensureContext();
    if (cue.type === 'noise') this._playNoise(ctx, cue);
    else this._playTone(ctx, cue, options?.pitchScale ?? 1);
  }

  _playTone(ctx, cue, pitchScale = 1) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = cue.type;
    // Small random detune per play (cue.pitchJitter) — identical repeated
    // blips read as "the same sample looping"; jitter makes a stream of
    // them (weapon fire, hit ticks) feel alive at zero extra cost.
    const jitter = (cue.pitchJitter ? 1 + (Math.random() * 2 - 1) * cue.pitchJitter : 1) * pitchScale;
    osc.frequency.setValueAtTime(cue.freq * jitter, ctx.currentTime);
    if (cue.sweepTo) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, cue.sweepTo * jitter), ctx.currentTime + cue.duration);
    }
    const vol = cue.volume * this._sfxBase * this.sfxVolume;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + cue.duration);
    osc.connect(gain).connect(this._sfxBus(ctx));
    osc.start();
    osc.stop(ctx.currentTime + cue.duration);
  }

  _playNoise(ctx, cue) {
    const bufferSize = Math.floor(ctx.sampleRate * cue.duration);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cue.filterFreq ?? 2000;

    const gain = ctx.createGain();
    const vol = cue.volume * this._sfxBase * this.sfxVolume;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + cue.duration);

    source.connect(filter).connect(gain).connect(this._sfxBus(ctx));
    source.start();
    source.stop(ctx.currentTime + cue.duration);
  }

  /**
   * Starts (or crossfades to) a theme from data/musicThemes.js at the given
   * intensity layer. Game.js calls this at run start and every stage
   * transition; intensity escalations mid-fight ride events (see
   * bindRunEvents). Calling with the already-playing theme only retunes
   * intensity — safe to call liberally.
   */
  playMusic(themeId = DEFAULT_MUSIC_THEME, intensity = 0) {
    if (this.muted) return;
    if (!this._music) this._music = new MusicDirector(() => this._ensureContext(), MUSIC_THEMES);
    this._music.setVolume(this._musicBase * this.musicVolume);
    this._music.play(themeId, intensity);
  }

  /** Ramps the current theme's layer mix (0 = waves, 1 = boss, 2 = phase 2+/desperation). No-op if music isn't playing. */
  setMusicIntensity(level) {
    if (this.muted) return;
    this._music?.setIntensity(level);
  }

  stopMusic() {
    this._music?.stop();
  }

  /** Wires this run's EventBus to the same cues every run uses. Call once per Game instance. */
  bindRunEvents(eventBus) {
    // --- combat: dealing damage ---
    eventBus.on('weapon:fired', () => this.playSfx('shoot'));
    eventBus.on('weapon:aoe-burst', () => this.playSfx('aoeBurst'));
    eventBus.on('boss:damaged', () => this.playSfx('hit'));
    eventBus.on('minion:damaged', () => this.playSfx('minionHit'));
    eventBus.on('minion:died', () => this.playSfx('enemyDeath'));
    eventBus.on('miniboss:died', () => this.playSfx('minibossDown'));
    eventBus.on('boss:died', () => this.playSfx('explosion'));

    // --- taking damage / defense ---
    eventBus.on('player:damaged', () => this.playSfx('playerHit'));
    eventBus.on('player:dodged', () => this.playSfx('dodge'));
    eventBus.on('bullet:grazed', () => this.playSfx('graze'));
    // Polarity (BULLET-HELL.md §D7): absorbing, and the release it banks toward.
    eventBus.on('polarity:absorbed', () => this.playSfx('absorb'));
    eventBus.on('polarity:released', () => this.playSfx('polarityRelease'));
    eventBus.on('player:dash-start', () => this.playSfx('dash'));
    eventBus.on('player:dash-refilled', () => this.playSfx('dashReady'));
    eventBus.on('player:healed', ({ amount }) => { if (amount > 0) this.playSfx('heal'); });
    // XP motes (BULLET-HEAVEN.md §H1): a pitch ladder rather than a repeated
    // blip. Consecutive pickups inside a short window walk up a pentatonic-ish
    // run and then reset, so a wave stage's steady trickle reads as a melody
    // going somewhere and a stage-clear vacuum reads as a flourish — which is
    // the difference between the microloop feeling generous and feeling noisy.
    // The rate limit on the cue itself (see data/sfx.js) still caps how many
    // actually sound; the ladder just makes the ones that do land musical.
    this._moteLadderStep = 0;
    this._moteLadderAt = 0;
    eventBus.on('mote:collected', () => {
      const now = performance.now();
      // A gap longer than a breath means a new phrase, not a continuation.
      if (now - this._moteLadderAt > 700) this._moteLadderStep = 0;
      this._moteLadderAt = now;
      this.playSfx('mote', { pitchScale: MOTE_LADDER[this._moteLadderStep % MOTE_LADDER.length] });
      this._moteLadderStep++;
    });
    eventBus.on('player:revived', () => this.playSfx('revive'));

    eventBus.on('weapon:fused', () => this.playSfx('fused'));
    // The announcement, not the event — quotes `fused`'s rising shape an
    // octave up and much quieter, so it reads as a promise of that cue.
    eventBus.on('fusion:eligible', () => this.playSfx('fusionReady'));

    // --- champion signatures & ultimates ---
    eventBus.on('combat:crit', () => this.playSfx('crit'));
    eventBus.on('player:plate-broken', () => this.playSfx('plateBreak'));
    eventBus.on('player:plate-restored', () => this.playSfx('plateRestore'));
    eventBus.on('player:plates-restored', () => this.playSfx('plateRestore'));
    eventBus.on('refrain:carry', () => this.playSfx('refrainCarry'));
    eventBus.on('player:cadence-max', () => this.playSfx('cadenceMax'));
    eventBus.on('ultimate:ready', () => this.playSfx('ultimateReady'));
    // Deathbomb (BULLET-HELL.md §D3) — see the two cues in data/sfx.js.
    eventBus.on('player:deathbomb-window', () => this.playSfx('deathbombWindow'));
    eventBus.on('player:deathbombed', () => this.playSfx('deathbomb'));
    eventBus.on('ultimate:activated', () => this.playSfx('ultimate'));
    eventBus.on('ultimate:ended', () => this.playSfx('ultimateEnd'));
    eventBus.on('ultimate:shockwave', () => this.playSfx('shockwave'));

    // --- boss beats (cue + music-intensity escalation) ---
    eventBus.on('boss:pattern-start', () => this.playSfx('warning'));
    eventBus.on('boss:pattern-captured', ({ desperation }) => this.playSfx(desperation ? 'captureDesperation' : 'capture'));
    eventBus.on('bullets:cancelled', () => this.playSfx('bulletCancel'));
    eventBus.on('boss:phase-change', ({ phase }) => {
      this.playSfx('phaseChange');
      // Phase 2 is the shared "second act" threshold every boss has —
      // thicken the mix from here on. The theme itself was already set by
      // Game.js at the stage transition.
      if (phase >= 2) this.setMusicIntensity(2);
    });
    eventBus.on('boss:vulnerability-open', () => this.playSfx('vulnOpen'));
    eventBus.on('boss:vulnerability-close', () => this.playSfx('vulnClose'));
    eventBus.on('boss:desperation-start', () => {
      this.playSfx('desperation');
      this.setMusicIntensity(2);
    });
    eventBus.on('desperation:end', () => this.playSfx('desperationEnd'));

    // --- waves / pickups / progression ---
    eventBus.on('wave:started', () => this.playSfx('waveStart'));
    eventBus.on('wave:cleared', () => this.playSfx('waveClear'));
    eventBus.on('pod:spawned', () => this.playSfx('podSpawn'));
    eventBus.on('pod:collected', () => this.playSfx('pickup'));
    // pillar:spawned stays silent on purpose — it lands the same moment as
    // the boss title/music hit and a third cue there is just clutter.
    eventBus.on('pillar:cracked', () => this.playSfx('pillarCrack'));
    eventBus.on('pillar:shattered', () => this.playSfx('pillarShatter'));
    eventBus.on('echo-rift:spawned', () => this.playSfx('echoRift'));
    eventBus.on('spoils:dropped', () => this.playSfx('spoilsDrop'));
    eventBus.on('echo-duel:accepted', () => this.playSfx('echoAccept'));
    eventBus.on('xp:levelup:open', () => this.playSfx('levelup'));

    // --- gauntlet interloper signatures (2026-07 bosses) ---
    eventBus.on('cadence:beat', () => this.playSfx('cadenceTick'));
    eventBus.on('cadence:surge', () => this.playSfx('cadenceSurge'));
    eventBus.on('boss:devoured', () => this.playSfx('devour'));
    eventBus.on('waltz:fling', () => this.playSfx('waltzFling'));
    eventBus.on('vow:broken', () => this.playSfx('vowBroken'));

    // --- ascension modifiers ---
    eventBus.on('ascension:enrage', () => this.playSfx('enrage'));
    eventBus.on('ascension:arena-pulse', () => this.playSfx('arenaPulse'));

    // The win/loss stinger lives on 'run:ended' only (not also on
    // player:died) so it fires exactly once per run, not twice.
    eventBus.on('run:ended', ({ won }) => this.playSfx(won ? 'victory' : 'defeat'));
  }
}

// Audio is a genuine cross-cutting concern — menu buttons, the run itself,
// and screens in between all need to make sound through one shared output
// (so e.g. mute actually mutes everything). A singleton here is deliberate,
// unlike the rest of the codebase's constructor-injected systems: it's the
// one system that outlives every other object's lifecycle, menu <-> run
// transitions included.
export const audio = new AudioSystem();
